/**
 * Analyse le Markdown que produit Luce, pour un rendu par composants Vue.
 *
 * On ne produit JAMAIS de HTML : la fonction rend un arbre de blocs et de
 * fragments que le composant interpole dans des balises qu'il choisit. Une
 * réponse contenant « <img src=x onerror=...> » ressort donc en texte, sans
 * qu'aucune sanitisation n'ait à intervenir — le projet n'embarque d'ailleurs
 * aucune bibliothèque pour ça.
 *
 * Le sous-ensemble couvert est celui que les modèles emploient réellement :
 * gras, italique, code, titres, listes et blocs de code. Le reste est affiché
 * tel quel, ce qui reste lisible.
 */

// Les liens en premier : sans quoi le soulignés ou les astérisques d'une URL
// seraient pris pour de l'emphase et couperaient l'adresse en morceaux.
const RE_INLINE = /(\[[^\]\n]+\]\([^)\s]+\)|`[^`\n]+`|\*\*[^*\n]+\*\*|__[^_\n]+__|\*[^*\n]+\*|(?<![a-zA-Z0-9_])_[^_\n]+_(?![a-zA-Z0-9_]))/g;

/**
 * Valide une adresse de lien, en liste blanche.
 *
 * Le contenu vient d'un modèle de langage : on ne cherche pas à repérer les
 * schémas dangereux (`javascript:`, `data:`…), on n'autorise que ce qu'on sait
 * inoffensif. Tout le reste est rendu en texte, sans lien.
 *
 * @param {string} raw
 * @returns {{href: string, external: boolean}|null}
 */
export function safeLink(raw) {
    // Les caractères de contrôle et les espaces exotiques servent à masquer un
    // schéma : un « javascript: » entrecoupé d'une tabulation passe une simple
    // comparaison de préfixe, alors que le navigateur, lui, l'interprète.
    // eslint-disable-next-line no-control-regex -- ces caracteres sont precisement ce qu'on retire
    const url = String(raw).replace(/[\u0000-\u0020\u00a0\u1680\u2000-\u200f\u2028-\u202f\u205f\u3000\ufeff]/g, '');

    if (url === '') return null;

    // Chemin interne. `//` est exclu : c'est une adresse absolue déguisée
    // (`//exemple.com`), qui sortirait de l'application sans le montrer.
    if (url.startsWith('/') && !url.startsWith('//')) {
        return { href: url, external: false };
    }

    try {
        const parsed = new URL(url);

        return ['http:', 'https:', 'mailto:'].includes(parsed.protocol)
            ? { href: parsed.href, external: true }
            : null;
    } catch {
        // Ni chemin interne, ni adresse analysable : on n'en fait pas un lien.
        return null;
    }
}

/**
 * Découpe une ligne en fragments typés.
 *
 * @param {string} line
 * @returns {Array<{type: 'text'|'strong'|'em'|'code'|'link', value: string, href?: string, external?: boolean}>}
 */
export function parseInline(line) {
    if (!line) return [];

    const parts = [];
    let last = 0;

    for (const match of line.matchAll(RE_INLINE)) {
        if (match.index > last) {
            parts.push({ type: 'text', value: line.slice(last, match.index) });
        }

        const token = match[0];

        if (token.startsWith('[')) {
            const [, label, target] = token.match(/^\[([^\]\n]+)\]\(([^)\s]+)\)$/);
            const link = safeLink(target);

            // Une adresse refusée reste lisible : on rend le Markdown tel quel
            // plutôt que d'escamoter le fait qu'un lien était proposé.
            parts.push(link
                ? { type: 'link', value: label, href: link.href, external: link.external }
                : { type: 'text', value: token });
        } else if (token.startsWith('`')) {
            parts.push({ type: 'code', value: token.slice(1, -1) });
        } else if (token.startsWith('**') || token.startsWith('__')) {
            parts.push({ type: 'strong', value: token.slice(2, -2) });
        } else {
            parts.push({ type: 'em', value: token.slice(1, -1) });
        }

        last = match.index + token.length;
    }

    if (last < line.length) {
        parts.push({ type: 'text', value: line.slice(last) });
    }

    return parts.length ? parts : [{ type: 'text', value: line }];
}

/** Les cellules d'une ligne « | a | b | », ou null si ce n'en est pas une. */
function tableRow(line) {
    const trimmed = line.trim();

    if (!trimmed.startsWith('|') || (trimmed.match(/\|/g) || []).length < 2) return null;

    // Les cellules sont ramenées à leur texte : « **Débit** » s'afficherait
    // sinon avec ses astérisques, le tableau ne passant pas par le rendu inline.
    return trimmed
        .replace(/^\||\|$/g, '')
        .split('|')
        .map((c) => parseInline(c.trim()).map((p) => p.value).join(''));
}

/** La ligne « |---|:--:| » sépare l'en-tête du corps : ce n'est pas une donnée. */
function isTableSeparator(cells) {
    return cells.length > 0 && cells.every((c) => /^:?-+:?$/.test(c));
}

/** Une ligne de liste : « - item », « * item », « 1. item ». */
function listItem(line) {
    const bullet = line.match(/^\s{0,3}[-*+]\s+(.*)$/);
    if (bullet) return { ordered: false, text: bullet[1] };

    const numbered = line.match(/^\s{0,3}(\d{1,3})[.)]\s+(.*)$/);
    if (numbered) return { ordered: true, text: numbered[2] };

    return null;
}

/**
 * Découpe un message en blocs affichables.
 *
 * @param {string} content
 * @returns {Array<Object>}
 */
export function parseBlocks(content) {
    const blocks = [];
    if (!content) return blocks;

    // Les blocs de code d'abord : leur contenu ne doit subir aucune autre analyse.
    const chunks = content.split('```');

    chunks.forEach((chunk, index) => {
        // Les rangs impairs sont à l'intérieur d'une paire de délimiteurs. Un
        // dernier rang impair signale une clôture manquante : c'est le cas
        // pendant le streaming, où le bloc s'écrit encore.
        if (index % 2 === 1) {
            const withoutLanguage = chunk.replace(/^[a-zA-Z0-9_+-]{0,20}\n/, '');
            blocks.push({ type: 'code', text: withoutLanguage.replace(/\n$/, '') });
            return;
        }

        const lines = chunk.split('\n');
        let paragraph = [];
        let list = null;
        let table = null;

        const flushParagraph = () => {
            if (paragraph.length) {
                blocks.push({ type: 'paragraph', lines: paragraph.map(parseInline) });
                paragraph = [];
            }
        };
        const flushList = () => {
            if (list) {
                blocks.push({ type: 'list', ordered: list.ordered, items: list.items.map(parseInline) });
                list = null;
            }
        };
        const flushTable = () => {
            if (table) {
                // Un tableau réduit à son en-tête n'en est pas un : on le rend en texte.
                blocks.push(table.rows.length
                    ? { type: 'table', header: table.header, rows: table.rows }
                    : { type: 'paragraph', lines: [parseInline(table.header.join(' | '))] });
                table = null;
            }
        };

        for (const rawLine of lines) {
            const line = rawLine.replace(/\s+$/, '');

            const cells = tableRow(line);

            if (cells) {
                flushParagraph();
                flushList();

                if (isTableSeparator(cells)) continue;

                if (!table) table = { header: cells, rows: [] };
                else table.rows.push(cells);

                continue;
            }

            flushTable();

            // Un séparateur horizontal ne porte rien : il alourdit une bulle de chat.
            if (/^\s{0,3}([-*_])\s*(\1\s*){2,}$/.test(line)) {
                flushParagraph();
                flushList();
                continue;
            }

            const heading = line.match(/^\s{0,3}(#{1,6})\s+(.*)$/);
            if (heading) {
                flushParagraph();
                flushList();
                blocks.push({
                    type: 'heading',
                    level: Math.min(heading[1].length + 2, 6),
                    parts: parseInline(heading[2]),
                });
                continue;
            }

            const item = listItem(line);
            if (item) {
                flushParagraph();
                if (list && list.ordered !== item.ordered) flushList();
                if (!list) list = { ordered: item.ordered, items: [] };
                list.items.push(item.text);
                continue;
            }

            if (line.trim() === '') {
                flushParagraph();
                flushList();
                continue;
            }

            flushList();
            paragraph.push(line);
        }

        flushParagraph();
        flushList();
        flushTable();
    });

    return blocks;
}
